import type { BarChartSeries, LineChartSeries } from '@mantine/charts'
import { BarChart, LineChart } from '@mantine/charts'
import { ActionIcon, Group, Paper, Stack, Tabs, Text, Title } from '@mantine/core'
import { IconChevronLeft, IconChevronRight, IconEyeExclamation, IconPencil } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { ResponsiveContainer } from 'recharts'

import { colors } from '@/configs/colors'
import { useGlobalState } from '@/hooks/useGlobalState'
import { formatChartDate, getExplicitDateRange, isBeforeCreationDate } from '@/utils/analytics'

import { NoDataAvailable } from './NoDataAvailable'
import { StatInfoGraphic } from './StatInfoGraphic'
import TabChartWrapper from './TabChartWrapper'

const TabKeys = {
  loginLineChart: 'loginLineChart',
  cmEditLineChart: 'cmEditLineChart',
  cmEditBarChart: 'cmEditBarChart',
} as const

const smeLoginsByDateSeries: LineChartSeries[] = [
  { name: 'total', color: colors.chartColors[0], label: 'Total Visits' },
]

const cmEditsByDateSeries: LineChartSeries[] = [{ name: 'total', color: colors.chartColors[0], label: 'Total Edits' }]

const editsPerCmSeries: BarChartSeries[] = [{ name: 'total', color: colors.chartColors[0], label: 'Total Edits' }]

export default function SmeMetrics() {
  const { api } = useGlobalState()
  const [activeTab, setActiveTab] = useState<string | null>(TabKeys.loginLineChart)
  const [barChartHeight, setBarChartHeight] = useState(300)
  const [backPeriod, setBackPeriod] = useState(0)

  const { data: sponsorInfo } = useQuery({
    queryKey: ['sponsor', 'info'],
    queryFn: () => api.getSponsorInfo(),
    staleTime: 5 * 60 * 1000,
  })

  const dateRange = useMemo(() => getExplicitDateRange(backPeriod), [backPeriod])

  const {
    data: smeLoginsByDate,
    isLoading: smeLoginsByDateLoading,
    error: smeLoginsByDateError,
  } = useQuery({
    queryKey: ['analytics', 'smeLoginsByDate', backPeriod],
    queryFn: () => api.getSmeLoginsByDate(dateRange.analyticsParams),
  })

  const {
    data: cmEditsPerSme,
    isLoading: cmEditsByDateLoading,
    error: cmEditsByDateError,
  } = useQuery({
    queryKey: ['analytics', 'cmEditsPerSme', backPeriod],
    queryFn: () => api.getCmEditsByDate(dateRange.analyticsParams),
  })

  const {
    data: editsPerCm,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['analytics', 'editsPerCm', backPeriod],
    queryFn: () => api.getEditsPerCm(dateRange.analyticsParams),
  })

  const handlePreviousPeriod = () => setBackPeriod(prev => prev - 1)
  const handleNextPeriod = () => setBackPeriod(prev => prev + 1)

  const nextPeriodDateRange = useMemo(() => getExplicitDateRange(backPeriod - 1), [backPeriod])
  const canGoPrevious = !isBeforeCreationDate(nextPeriodDateRange.startDate, sponsorInfo?.createdAt)
  const canGoNext = backPeriod < 0

  const { data: contentModulesData } = useQuery({
    queryKey: ['cm', 'list'],
    queryFn: () => api.getContentModules(),
    initialData: [],
  })

  // Process chart data by matching entries with content modules by taskId,
  // replacing "Unknown" labels with actual titles, filtering out removed/unmatched entries
  const processedEditsPerCm = useMemo(() => {
    if (!editsPerCm?.data || !contentModulesData?.length) {
      return editsPerCm?.data || []
    }

    return editsPerCm.data
      .map(entry => {
        const matchingContentModule = contentModulesData.find(cm => cm.taskId === entry.taskId)

        if (matchingContentModule) {
          // Filter out Untitled modules with status "removed" (most likely deleted before even saving as draft)
          if (matchingContentModule.status === 'removed' && entry.label === 'Unknown') {
            console.log('[SmeMetrics] 🚫 Filtered out untitled CM with status "removed":', {
              entry,
              matchingContentModule,
            })
            return null
          }

          if (entry.label === 'Unknown') {
            const cmTitle = matchingContentModule.title || matchingContentModule.name || 'Unknown'
            if (cmTitle !== 'Unknown') {
              // Replace "Unknown" labels with actual titles if they exist (this isn't likely to happen, mostly precautionary)
              console.log('[SmeMetrics] 🎯 Unknown label Matched and Updated', {
                unknownEntry: entry,
                matchingContentModule,
              })
              return {
                ...entry,
                label: cmTitle,
              }
            } else {
              // If we can't find a real title, filter out the entry
              return null
            }
          }
        } else {
          // Log when no match is found for any entry
          console.warn('[SmeMetrics] ❌ No match found for entry:', entry)
          return null
        }

        return entry
      })
      .filter(entry => entry !== null) // Remove null entries (filtered out or unmatched entries)
  }, [editsPerCm?.data, contentModulesData])

  useEffect(() => {
    if (processedEditsPerCm.length) {
      setBarChartHeight(processedEditsPerCm.length * 40)
    }
  }, [processedEditsPerCm.length])

  const hasEditsData = processedEditsPerCm.length > 0

  return (
    <Stack>
      <Paper radius="md" p="lg" shadow="sm" px="xl">
        <Group justify="space-between" mb="md" align="top">
          <Group gap="xl">
            <StatInfoGraphic
              icon={IconEyeExclamation}
              label="Expert Visits"
              value={smeLoginsByDate?.stats.total ?? 0}
              tooltipText="Total number of Expert visits to Portal"
            />
            <StatInfoGraphic
              icon={IconPencil}
              label="CM Edits"
              value={editsPerCm?.stats.total ?? 0}
              tooltipText="Total number of Content Module edits"
            />
          </Group>

          <Group gap="xs" align="center">
            <ActionIcon
              variant="subtle"
              onClick={handlePreviousPeriod}
              disabled={!canGoPrevious}
              aria-label="Previous period">
              <IconChevronLeft size={20} />
            </ActionIcon>

            <Text size="sm" c="dimmed" style={{ minWidth: 180, textAlign: 'center' }}>
              {formatChartDate(dateRange.startDate, true)} — {formatChartDate(dateRange.endDate, true)}
            </Text>

            <ActionIcon variant="subtle" onClick={handleNextPeriod} disabled={!canGoNext} aria-label="Next period">
              <IconChevronRight size={20} />
            </ActionIcon>
          </Group>
        </Group>
      </Paper>

      <Paper radius="md" p="lg" shadow="sm" px="xl">
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value={TabKeys.loginLineChart} leftSection={<IconEyeExclamation size={16} />}>
              Expert Visits
            </Tabs.Tab>
            <Tabs.Tab value={TabKeys.cmEditLineChart} leftSection={<IconPencil size={16} />}>
              Expert Edits
            </Tabs.Tab>
            <Tabs.Tab value={TabKeys.cmEditBarChart} leftSection={<IconPencil size={16} />}>
              CM Edits
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value={TabKeys.loginLineChart} py="md">
            <Title order={6} mb="md">
              Number of Portal Visits by Experts
            </Title>
            <TabChartWrapper
              isActive={activeTab === TabKeys.loginLineChart}
              isLoading={smeLoginsByDateLoading}
              error={smeLoginsByDateError}>
              <LineChart
                h={300}
                data={smeLoginsByDate?.data ?? []}
                dataKey="date"
                series={smeLoginsByDateSeries}
                curveType="linear"
                xAxisProps={{
                  tickFormatter: (date: string) => {
                    return formatChartDate(date)
                  },
                  angle: -45,
                  textAnchor: 'end',
                  interval: 0,
                }}
              />
            </TabChartWrapper>
          </Tabs.Panel>
          <Tabs.Panel value={TabKeys.cmEditLineChart} py="md">
            <Title order={6} mb="md">
              Number of Edits by Experts
            </Title>
            <TabChartWrapper
              isActive={activeTab === TabKeys.cmEditLineChart}
              isLoading={cmEditsByDateLoading}
              error={cmEditsByDateError}>
              <LineChart
                h={300}
                data={cmEditsPerSme?.data ?? []}
                dataKey="date"
                series={cmEditsByDateSeries}
                curveType="linear"
                xAxisProps={{
                  tickFormatter: (date: string) => {
                    return formatChartDate(date)
                  },
                  angle: -45,
                  textAnchor: 'end',
                  interval: 0,
                }}
              />
            </TabChartWrapper>
          </Tabs.Panel>
          <Tabs.Panel value={TabKeys.cmEditBarChart} py="md">
            <Title order={6} mb="md">
              Total Edits to Each Content Module
            </Title>
            <TabChartWrapper isActive={activeTab === TabKeys.cmEditBarChart} isLoading={isLoading} error={error}>
              <ResponsiveContainer width="100%" height={barChartHeight}>
                {hasEditsData ? (
                  <BarChart
                    data={processedEditsPerCm || []}
                    dataKey="label"
                    type="stacked"
                    orientation="vertical"
                    series={editsPerCmSeries}
                    gridAxis="none"
                    yAxisProps={{
                      interval: 0,
                      tickFormatter: (value: string, _: number) => {
                        const maxChars = 24
                        return value.length > maxChars ? value.substring(0, maxChars - 3) + '...' : value
                      },
                      width: 150,
                      tick: {
                        textAnchor: 'start',
                        dx: -140,
                        fontSize: 12,
                      },
                    }}
                  />
                ) : (
                  <NoDataAvailable />
                )}
              </ResponsiveContainer>
            </TabChartWrapper>
          </Tabs.Panel>
        </Tabs>
      </Paper>
    </Stack>
  )
}
